import { Button, Modal, SizeValue, Spinner, useMediaQuery } from '@ag1/ui';
import { toPlainText } from '@portabletext/react';
import { usePageContext } from '@portal/processes/Page/PageContext';
import ImageModal from '@portal/shared/modals/ImageModal/ImageModal';
import { buttonClickEvent, clickPurchaseIntentEvent } from 'analytics/events/studio';
import clsx from 'clsx';
import React, { SyntheticEvent, useCallback, useMemo, useState } from 'react';
import { useGoToOffersV2 } from 'shared/hooks/useGoToOffersV2';
import { useGoToSection } from 'shared/hooks/useGoToSection';
import { useLocale } from 'shared/hooks/useLocale';
import IngredientModal from 'shared/modals/IngredientModal/IngredientModal';
import CustomPortableText from 'shared/studio/PortableText/PortableText';
import { getSanityLinkUrl } from 'shared/utils/getSanityLinkUrl';
import { getThemeCSSClass } from 'shared/utils/getThemeCSSClass';
import { useStoreValue } from 'state/hooks/useStore';
import type { UnifiedButton as UnifiedButtonType } from 'types/studio/studio';
import styles from './UnifiedButton.module.scss';

type UnifiedButtonProps = {
  unifiedButton?: UnifiedButtonType | null;
};

type Props = {
  onClick?: (event: SyntheticEvent) => void;
  sectionType: string;
  className?: string;
  dataTestId?: string;
  ignoreLink?: boolean;
  size?: SizeValue | ResponsiveSizeValue<SizeValue>;
  theme?: 'old' | 'new';
  disableButtonEvents?: boolean;
  isLoading?: boolean;
  isDisabled?: boolean;
  unifiedButton: UnifiedButtonType;
};

const UnifiedButtonComponent = ({
  unifiedButton,
  sectionType,
  dataTestId,
  className,
  size,
  onClick,
  theme,
  ignoreLink,
  disableButtonEvents,
  isLoading,
  isDisabled
}: Props) => {
  const { maybeReplaceOfferlessCheckoutLink } = usePageContext();
  const mediaQuery = unifiedButton.textChangeResolution || 's';
  const atMedia = useMediaQuery(`${mediaQuery}`);
  const store = useStoreValue();

  const setButtonText = (button: any, mobileText: any) => {
    let finalText = <CustomPortableText value={button} />;
    if (unifiedButton.useMobileButtonText && unifiedButton.mobileButtonText && !atMedia) {
      finalText = <CustomPortableText value={mobileText} />;
    }
    return finalText;
  };

  const locale = useLocale();

  const buttonLink = useMemo(() => {
    return maybeReplaceOfferlessCheckoutLink(
      getSanityLinkUrl(unifiedButton?.baseButtonLinkV2?.btnLink, locale)
    );
  }, [locale, maybeReplaceOfferlessCheckoutLink, unifiedButton?.baseButtonLinkV2?.btnLink]);

  const { goToOffer } = useGoToOffersV2({
    withScroll: true,
    bundleUrl: unifiedButton.bundleUrl,
    buttonLink: unifiedButton.buttonLink
  });

  const sendButtonClickEvent = useCallback(
    (
      button: NonNullable<
        | UnifiedButtonType['baseButtonLinkV2']
        | UnifiedButtonType['scrollButton']
        | UnifiedButtonType['openModalButton']
        | UnifiedButtonType['goToOfferButton']
      >,
      url?: string,
      skipPurchaseIntent?: boolean
    ) => {
      const buttonText = button?.portableText ? toPlainText(button?.portableText) : '';
      buttonClickEvent({
        clickText: buttonText,
        url,
        location: sectionType
      });
      if (!skipPurchaseIntent) {
        clickPurchaseIntentEvent({
          store,
          clickText: buttonText,
          location: sectionType,
          url
        });
      }
    },
    [sectionType, store]
  );

  const { goToSection } = useGoToSection(`${unifiedButton.scrollToID}`, {
    setFocusAfterScroll: true
  });

  const handleButtonClickSection = useCallback(() => {
    goToSection();
  }, [goToSection]);

  const [isOpen, setOpen] = useState(false);

  const button = useMemo(() => {
    if (unifiedButton.switchButton === 'openModalButton' && unifiedButton.openModalButton) {
      return unifiedButton.openModalButton;
    }
    if (unifiedButton.switchButton === 'scrollButton' && unifiedButton.scrollButton) {
      return unifiedButton.scrollButton;
    }
    if (unifiedButton.switchButton === 'goToOfferButton' && unifiedButton.goToOfferButton) {
      return unifiedButton.goToOfferButton;
    }
    if (unifiedButton.switchButton === 'link' && unifiedButton.baseButtonLinkV2) {
      return unifiedButton.baseButtonLinkV2;
    }
    return null;
  }, [unifiedButton]);

  const handleButtonClick = useCallback(
    (event: SyntheticEvent) => {
      if (onClick) {
        onClick(event);
      }
      if (unifiedButton.switchButton === 'openModalButton' && unifiedButton.openModalButton) {
        setOpen(true);
        if (!disableButtonEvents) {
          sendButtonClickEvent(unifiedButton.openModalButton);
        }
      }
      if (unifiedButton.switchButton === 'scrollButton' && unifiedButton.scrollButton) {
        handleButtonClickSection();
        if (!disableButtonEvents) {
          sendButtonClickEvent(unifiedButton.scrollButton, undefined, true);
        }
      }
      if (unifiedButton.switchButton === 'goToOfferButton' && unifiedButton.goToOfferButton) {
        sendButtonClickEvent(unifiedButton.goToOfferButton, undefined, true);
        goToOffer();
      }
      if (unifiedButton.switchButton === 'link' && unifiedButton.baseButtonLinkV2) {
        if (!disableButtonEvents) {
          sendButtonClickEvent(unifiedButton.baseButtonLinkV2, buttonLink);
        }
      }
    },
    [
      onClick,
      unifiedButton,
      buttonLink,
      sendButtonClickEvent,
      handleButtonClickSection,
      goToOffer,
      disableButtonEvents
    ]
  );

  return (
    <>
      {unifiedButton.switchButton !== 'noButton' && button && (
        <Button
          size={size}
          href={unifiedButton.switchButton === 'link' && !ignoreLink ? buttonLink : ''}
          target={
            unifiedButton.switchButton === 'link' && unifiedButton.baseButtonLinkV2?.targetBlank
              ? '_blank'
              : '_self'
          }
          onClick={handleButtonClick}
          dataTestId={dataTestId}
          data-gtm-skip-global
          variant={button.variant}
          icon={button.showIcon && !isLoading && button.iconOptions?.icon}
          iconPosition={button.iconOptions?.position}
          rounded
          className={clsx(
            className,
            button.variant === 'primary' && unifiedButton.hoverColor === 'dark' && styles.dark,
            getThemeCSSClass(theme)
          )}
          theme={theme}
          disabled={isLoading || isDisabled}>
          {isLoading ? (
            <Spinner color="brand-white" size={24} withOverlay={false} />
          ) : (
            setButtonText(button.portableText, unifiedButton.mobileButtonText)
          )}
        </Button>
      )}
      {unifiedButton.switchButton === 'openModalButton' && (
        <Modal isOpen={isOpen} onClose={() => setOpen(false)} mode="fullScreen">
          {unifiedButton.openModalSelect === 'ingredientsModal' && (
            <IngredientModal
              isOpen={isOpen}
              onClose={() => setOpen(false)}
              isV53={unifiedButton.v53Toggle}
              ingredientProducts={unifiedButton.ingredientProducts}
              supplementType={unifiedButton.supplementType}
              showFlavors={unifiedButton.showFlavors}
            />
          )}
          {unifiedButton?.openModalSelect === 'ingredients' && (
            <ImageModal onClose={() => setOpen(false)} modalImage={unifiedButton?.modalImage} />
          )}
        </Modal>
      )}
    </>
  );
};

type PropsForWrapper = {
  onClick?: (event: SyntheticEvent) => void;
  sectionType: string;
  className?: string;
  dataTestId?: string;
  ignoreLink?: boolean;
  size?: SizeValue | ResponsiveSizeValue<SizeValue>;
  theme?: 'old' | 'new';
  disableButtonEvents?: boolean;
  isLoading?: boolean;
  isDisabled?: boolean;
} & UnifiedButtonProps;

const UnifiedButton: React.FC<PropsForWrapper> = ({ unifiedButton, ...restProps }) => {
  if (!unifiedButton) {
    return null;
  }

  return <UnifiedButtonComponent {...restProps} unifiedButton={unifiedButton} />;
};

export default UnifiedButton;
